import { useState } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import EmptyGrowthMessage from '~/components/blocks/EmptyGrowthMessage/EmptyGrowthMessage'
import GrowthStatus from '~/components/blocks/GrowthStatus/GrowthStatus'
import {
  type TradingFloorItemProps,
  TradingFloorRelativeDate,
} from '~/components/blocks/TradingFloorList/TradingFloorList'
import type { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import capFirstLetter from '~/utils/capFirstLetter'
import { getArtworkPrice } from '~/utils/getArtworkPrice'
import ticketStatusMapper, {
  TradingTicketStatus,
} from '~/utils/ticketStatusMapper'
import { toURL } from '~/utils/urlResolver'

import ArtworkCardPortfolio from '../../blocks/ArtworkCardPortfolio/ArtworkCardPortfolio'
import Button, { ButtonTypes, ButtonVariants } from '../Button/Button'
import Link from '../Link/Link'
import PulsingDot, { DotSize } from '../PulsingDot/PulsingDot'
import SpecificImage from '../SpecificImage/SpecificImage'
import { ExtendedTableContent } from '../Table/Table'
import Text, { TextSizes } from '../Text/Text'

import styles from './TradingFloorCard.module.css'

export interface TradingFloorCardProps {
  item: TradingFloorItemProps
  openForm: (supplyProps: FormTypes, item: TradingFloorItemProps) => void
  testID?: string
}

export default function TradingFloorCard({
  item,
  openForm,
  testID,
}: TradingFloorCardProps) {
  const tCommon = useTranslations('common')
  const tValuation = useTranslations('valuation')
  const tPortfolio = useTranslations('portfolio')
  const [expandedState, setExpandedState] = useState<boolean>(false)
  const price = getArtworkPrice(item.listPriceMin, item.listPriceMax, item.poa)
  const status: any = item.ticketStatus
  const supplyProps = ticketStatusMapper(status, tCommon)
  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()

  return (
    <div className={styles.base} data-testid={testID}>
      <div className={styles.header}>
        <div className={styles.status}>
          {supplyProps.title === 'For sale' && (
            <PulsingDot size={DotSize.small} />
          )}
          <Text size={TextSizes.bodySml} className={styles.text} uppercase>
            {supplyProps.title}
          </Text>
        </div>

        <div className={styles.buttonContainer}>
          <TradingFloorRelativeDate date={item.dateModified} />

          <ArtworkCardPortfolio
            artworkId={item.artworkId}
            artworkImage={item.image}
            artistId={item.artistId}
            artistName={item.artist}
            artworkName={item.title}
            eventLocation={`Trading Floor Tile`}
            artworkDetails={item}
          />
        </div>
      </div>

      <Link
        href={toURL({
          slug: item.artworkSlug,
          artist: item.artistSlug,
          collection: item.collectionSlug,
          collectionTaxonomy: item.collectionTaxonomy,
          _type: 'artwork',
        })}
      >
        <div className={styles.ticketDetails}>
          {item.image && item.image.asset && (
            <div className={styles.imageWrap}>
              <SpecificImage
                asset={item.image.asset}
                alt={`${item.title} - ${item.format}`}
                width={106}
              />
            </div>
          )}
          <div>
            <Text
              size={TextSizes.tertiaryTitleMed}
              Tag="h4"
              className={styles.artworkTitle}
            >
              {item.title}
            </Text>
            <Text
              size={TextSizes.secondaryTitleSml}
              uppercase
              Tag="p"
              className={styles.format}
            >
              {item.format}
            </Text>
            <Text
              size={TextSizes.primaryTitleXSml}
              Tag="p"
              className={styles.artistSpacing}
            >
              {item.artist}
            </Text>
            <div className={styles.ticketDetailsItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tValuation('valueIndicator')}
              </Text>
              <Text size={TextSizes.primaryTitleXXSml}>{price}</Text>
            </div>
            {item.aagr ? (
              <div className={styles.ticketDetailsItem}>
                <Text size={TextSizes.secondaryTitleMicro}>
                  {tPortfolio('growthAAGR5Years')}
                </Text>
                <GrowthStatus size="small" growth={+item.aagr} />
              </div>
            ) : (
              <div
                className={classNames(styles.ticketDetailsItem, {
                  [styles.clickable]: true,
                })}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setExpandedState(!expandedState)
                }}
              >
                <Text size={TextSizes.bodySml}>
                  {tPortfolio('growthAAGR5Years')}
                </Text>

                <GrowthStatus
                  size="medium"
                  growth={undefined}
                  graphGrey
                  showInfoIcon
                  showEmptyText
                />
              </div>
            )}
          </div>
        </div>
      </Link>
      {expandedState && (
        <div className={styles.expandedContent}>
          <ExtendedTableContent
            content={<EmptyGrowthMessage />}
            setExpandedState={() => setExpandedState(false)}
          />
        </div>
      )}
      <Button
        button={{
          type: ButtonTypes.button,
          onClick: async () => {
            openForm(supplyProps.formType, item)
            handleFormLaunchFromCtaAnalytics(
              `Clicked Enquire to ${capFirstLetter(supplyProps.formType)}`,
              supplyProps.formType,
              'Trading Floor',
              item.artist,
              item.artistId,
              item.title,
              item.artworkId
            )
          },
        }}
        variant={
          item.ticketStatus === TradingTicketStatus.sell
            ? ButtonVariants.primaryDark
            : ButtonVariants.secondaryDark
        }
        smallFont
        iconLeft
        className={styles.cardButton}
      >
        {supplyProps.buttonText}
      </Button>
    </div>
  )
}
